import { useEffect, useMemo, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CaseIdSchema, type Source } from '@casetrace/shared';
import { X } from 'lucide-react';
import { deleteSource, getImporterBaseUrl, startImport } from '../../lib/callables';
import { Button } from '../../components/ui/button';
import { auth } from '../../lib/firebase';
import { useCaseSources } from '../../hooks/use-case-sources';
import { useImportJobs } from '../../hooks/use-import-jobs';
import { IphoneSmsImportSection } from './iphone-sms/iphone-sms-import-section';
import { WhatsappUploadButton } from './whatsapp-upload-button';
import { DocumentUploadButton } from './document-upload-button';
import { PstUploadButton } from './pst-upload-button';
import { JobsListSection } from './jobs-list-section';
import { SourcesListSection } from './sources-list-section';
import { SourceTypeIcon } from './source-type-icon';
import { bySource, sortJobs } from './sources-panel-helpers';

type SourcesPanelProps = {
  readonly caseId: string;
  readonly onClose: () => void;
};

export function SourcesPanel({ caseId, onClose }: SourcesPanelProps) {
  const parsedCaseId = CaseIdSchema.parse(caseId);
  const sourcesQuery = useCaseSources(parsedCaseId);
  const jobsQuery = useImportJobs(parsedCaseId);
  const sources = sourcesQuery.data ?? [];
  const jobs = jobsQuery.data ?? [];
  const rows = useMemo(() => bySource(sources, jobs), [sources, jobs]);
  const recentJobs = useMemo(() => sortJobs(jobs).slice(0, 8), [jobs]);
  const sourcesById = useMemo(
    () => new Map(sources.map((source) => [source.id, source])),
    [sources],
  );

  const ownerUid = auth.currentUser?.uid;
  const autoStarted = useRef(new Set<string>());
  const connectToM365 = () => {
    if (!ownerUid) return;
    const oauthUrl = new URL(`${getImporterBaseUrl()}/oauth/m365/start`);
    oauthUrl.searchParams.set('caseId', parsedCaseId);
    oauthUrl.searchParams.set('navn', 'Microsoft 365');
    oauthUrl.searchParams.set('owner', ownerUid);
    window.location.href = oauthUrl.toString();
  };
  const startImportMutation = useMutation({
    mutationFn: (sourceId: Source['id']) => startImport({ caseId: parsedCaseId, sourceId }),
  });

  useEffect(() => {
    for (const { source, latestJob } of rows) {
      if (source.type !== 'm365') continue;
      if (source.status !== 'pending') continue;
      if (latestJob !== null) continue;
      if (autoStarted.current.has(source.id)) continue;
      autoStarted.current.add(source.id);
      startImportMutation.mutate(source.id);
    }
  }, [rows]);

  const deleteSourceMutation = useMutation({
    mutationFn: (sourceId: Source['id']) => deleteSource({ caseId: parsedCaseId, sourceId }),
  });

  return (
    <aside
      className="fixed inset-y-0 right-0 z-30 flex w-full max-w-md flex-col border-l shadow-2xl"
      style={{ background: 'var(--color-surface)', borderColor: 'rgba(255,255,255,0.07)' }}
    >
      <div
        className="flex shrink-0 items-center justify-between border-b px-4 py-3"
        style={{ borderColor: 'rgba(255,255,255,0.07)' }}
      >
        <div>
          <h2 className="text-sm font-semibold text-[#ededf0]">Kilder</h2>
          <p className="mt-0.5 text-xs text-[#5e6070]">
            Koble til M365, last opp WhatsApp ZIP, Outlook PST, iPhone SMS-backup eller dokumenter
            for import.
          </p>
        </div>
        <button
          onClick={onClose}
          className="rounded-md p-1 text-[#5e6070]"
          aria-label="Lukk kildepanel"
        >
          <X size={15} />
        </button>
      </div>
      <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
        <Button variant="outline" disabled={!ownerUid} onClick={connectToM365}>
          <SourceTypeIcon type="m365" />
          Koble til Microsoft 365
        </Button>
        <WhatsappUploadButton caseId={parsedCaseId} />
        <PstUploadButton caseId={parsedCaseId} />
        <IphoneSmsImportSection caseId={parsedCaseId} />
        <DocumentUploadButton caseId={parsedCaseId} />
        {deleteSourceMutation.error && (
          <p className="text-xs text-danger/80">Kunne ikke slette kilde. Prøv igjen.</p>
        )}
        <div className="mt-2 flex flex-col gap-5">
          <SourcesListSection
            rows={rows}
            isLoading={sourcesQuery.isLoading}
            isDeleting={deleteSourceMutation.isPending}
            hasError={Boolean(startImportMutation.error)}
            onDelete={(sourceId) => deleteSourceMutation.mutate(sourceId)}
          />
          <JobsListSection
            jobs={recentJobs}
            sourcesById={sourcesById}
            isLoading={jobsQuery.isLoading}
          />
        </div>
      </div>
      <div
        className="shrink-0 border-t px-4 py-2"
        style={{ borderColor: 'rgba(255,255,255,0.07)' }}
      >
        <Button className="w-full" variant="ghost" onClick={onClose}>
          Lukk
        </Button>
      </div>
    </aside>
  );
}
